// NB imported by path, not via the `@frontend/api` barrel.
import {
  GEOMETRIC_ANALYSIS_PROP_TYPE,
  type GeometricAnalysisPropType,
} from '@frontend/api/src/parts/geometricAnalysis/types';

import type { ColorRGBArray } from './colors';

export const MM_PER_INCH = 25.4;
export const KG_PER_LB = 0.4535924;
export const SPLIT_CHAR = '|';
export const FRAMERATE = 60;
export const SNAPSHOT_TIME_DELTA = 1; // seconds between viewer state snapshot captures
export const LOAD_TIME_WORLD = 0.1;
export const RUN_TIME_INITIAL = 400; // milliseconds ToDo: come up with a regression here based on total number of entities to render
//Note: LOAD_TIME_PER_FACE_EDGE_OR_VERTEX does not account for number tessellations per face or edge
export const LOAD_TIME_PER_FACE_EDGE_OR_VERTEX = 0.1; // load time per face, edge or vertex for body in milliseconds
export const ADDITIONAL_EDGE_LOAD_TIME = 0.0001;
export const ENTITY_COUNT_FOR_CALIPER_WARN = 4000; // if entity count higher than this warn user performance will be very limited
export const MAX_ESTIMATED_LOAD_THRESHOLD = 100; // maximum estimated load time in seconds before we limit loading things

// NOTE: this will dictate the integrity of zoomed in comment
// states moving forward, DO NOT CHANGE THIS
export const BOUNDBOX_VIEWPORT_FACTOR = 2.25;

export const FEATURE_INDICATOR = 'fea';
export const FEEDBACK_INDICATOR = 'fee';

// exported as 'Paperless-Parts-Default', 'Paperless-Parts-Deleted'
// labeled as 'File Layers'
export const VECTORIZATION_SPECIAL_LAYERS = {
  DEFAULT: {
    key: 'Paperless-Parts-Default',
    label: 'Default',
    color: 0x262626,
  },
  DELETED: {
    key: 'Paperless-Parts-Deleted',
    label: 'Deleted',
    color: 0x000000,
  },
};

// exported as name, labeled as label, painted as color
export const VECTORIZATION_EXPORT_LAYERS: {
  key: string;
  label: string;
  color: ColorRGBArray;
  //StepTextModel does not recognize ColorRGBArray, which is 0..1 scale for components. It only recognizes integers 0..255.
  //So define hexColor as number for use with painting text.
  hexColor: number;
}[] = [
  //Colors are RGB values between 0 and 1 for colors BufferAttribute
  {
    key: 'Paperless-Parts-Cut',
    label: 'Cut',
    hexColor: 0xe11d1d,
    color: [0.8823529411764706, 0.11372549019607843, 0.11372549019607843],
  },
  // { key: 'Paperless-Parts-Bend', label: 'Bend', color: [0.592156862745098,0.2784313725490196,1] /*0x9747ff*/ }, // Turned off for Vector Editor Milestone 1
  // { key: 'Paperless-Parts-Etch', label: 'Etch', color: [0.6941176470588235,0.8862745098039215,1] /*0xb1e2ff*/ }, // Turned off for Vector Editor Milestone 1
];

export const EXPORT_LAYER_KEYS = VECTORIZATION_EXPORT_LAYERS.map(
  (layer) => layer.key,
);

export const enum PART_VIEWER_MODES {
  LITE = 'LITE',
  FULL = 'FULL',
  VECTOR_EDIT = 'VECTOR_EDIT',
}

// A const object rather than a `const enum`, so the app's `'in' | 'mm'` string
// unions (redux display units, part file units) are assignable to it.
export const UNITS = {
  IN: 'in',
  MM: 'mm',
  NATIVE: 'native',
} as const;
export type UNITS = (typeof UNITS)[keyof typeof UNITS];

export const enum WEIGHT_UNITS {
  KG = 'kg', //mass interpretted as weight
  LB = 'lb',
}

export const enum UNIT_SYSTEM {
  IMPERIAL = 'imperial',
  METRIC = 'metric',
}

export const enum ENVIRONMENT_TYPES {
  PROD = 'PROD',
  DEV = 'DEV',
}

export const enum FEATURE_LOADING_TYPES {
  FEATURE = 'FEATURE',
  FEEDBACK = 'FEEDBACK',
}

export const TWEEN_TIMES = {
  DEFAULT: 750,
  FEATURE_SNAP_TO: 750,
  FOCUS: 750,
  FORCE: 100,
  NORMAL_TO: 500,
  RECENTER: 375,
  RESET_VIEW: 0,
  STORED_VIEW_SNAP_TO: 750,
  ZOOM_TO_FIT: 500,
  CUBE_VIEW_SNAP_TO: 750,
  CUBE_COLOR: 100,
  CUBE_RECOLOR: 550,
};

export const enum FORCE_VIEW_TYPES {
  NORMAL_TO = 'NORMAL_TO',
  RESET_VIEW = 'RESET_VIEW',
  ZOOM_TO_FIT = 'ZOOM_TO_FIT',
  ZOOM_TO_SELECTION = 'ZOOM_TO_SELECTION',
}

export const enum PART_TYPES {
  DXF = 'dxf',
  STEP = 'step',
  STL = 'stl',
}

export const enum ENTITY_TYPES {
  EDGE = 'edge',
  FACE = 'face',
  VERTEX = 'vertex',
  TEXT = 'text',
  REF = 'ref',
  STL = 'stl',
  SimpRep = 'simpRep',
}

export const MOBILE_DEFAULTS = {
  DOUBLE_TAP_TIMEOUT: 200,
  LONG_PRESS_TIMEOUT: 400,
};

export const AXES_DEFAULTS = {
  AXES_SIZE: 150, //width and height of cube
  CAM_DISTANCE: 30,
};

export const CUBE_DEFAULTS = {
  CUBE_LENGTH: 10,
  CUBE_EDGE: 2.2,
  CUBE_THICKNESS: 0.5,
  CUBE_OPACITY: 0.75,
  CUBE_AXES_OVERHANG: 2,
};

export const PERSPECTIVE_CAMERA_DEFAULTS = {
  VERTICAL_FOV: 50,
};

export const ORTHOGRAPHIC_CAMERA_DEFAULTS = {
  MAX_SNAP_ZOOM: 8.0,
  MAX_ZOOM: Infinity,
  MIN_ZOOM: 0,
  FAR: 100000,
};

export const TRACKBALL_DEFAULTS = {
  DYNAMIC_DAMPING_FACTOR: 0.99,
  ROTATE_SPEED: 3.0,
  STATIC_MOVING: true,
  ZOOM_SPEED: 2.0,
};

export const ARROW_DEFAULTS = {
  CYLINDER_HEIGHT: 0.83,
  CYLINDER_RADIUS: 0.033,
  CONE_HEIGHT: 0.33,
  CONE_RADIUS: 0.083,
};

export const enum STOCK_TYPES {
  CYLINDRICAL = 'cylindrical',
  RECTANGULAR = 'rectangular',
  SHEET = 'sheet',
}

export const enum OPACITY_MAP {
  BODY_DEFAULT = 1,
  EXCLUDED_EDGE = 0.5,
  HIGHLIGHT_ONLY_VIEW = 0.3,
  HOVER_OBJECT = 0.6,
  SELECTED_OBJECT = 0.6,
  UNFOCUSED = 0.2,
  VERTEX = 0.0,
  WIREFRAME = 0.1,
}

export const MATERIAL_PROPERTIES = {
  COLOR: 0xa9afaf,
  OPACITY: OPACITY_MAP.BODY_DEFAULT,
  REFLECTIVITY: 0.1,
  METALNESS: 0.1,
  DXF_PART_LINEWIDTH: 1.25, // Screen-space line width for flat-part mesh lines (LineSegments2)
  ADDITIONAL_FEEDBACK_LINEWIDTH: 5,
  EMMISSIVE_COLOR: 0xf4ed57,
};

const LEGACY_KEYS = {
  bad_bezier: 'bad_bezier',
  bad_extrusion: 'bad_extrusion',
  bad_faces: 'bad_faces',
  bad_spline: 'bad_spline',
  bend_item: 'bend_item',
  bounding_box: 'bounding_box',
  bounding_plane_excess: 'excess_bounding_planes',
  bounding_plane_insufficient: 'insufficient_bounding_planes',
  cone_chamfer: 'cone_chamfer',
  cone_draft: 'cone_draft',
  external_radius: 'external_radius',
  flat: 'flat',
  flat_part_cavity: 'cavity',
  illegal_hole: 'illegal_hole',
  large_internal_plane: 'large_internal_plane',
  non_uniform: 'non_uniform',
  non_vertical_cone: 'non_vertical_cone',
  non_vertical_cylinder: 'non_vertical_cylinder',
  offset: 'offset',
  slanted: 'slanted',
  small_cone: 'small_cone',
  small_feature: 'small_feature',
  tight_corner_mild: 'tight_corner_mild',
  tipped: 'tipped',
  unpaired_cone: 'unpaired_cone',
  unpaired_frustum: 'unpaired_frustum',
};

const SETUP_LABELS = {
  lathe_setup1: 'lathe_setup1',
  lathe_setup2: 'lathe_setup2',
  side_1: 'Side1',
  side_2: 'Side2',
  side_a: 'SideA',
  side_b: 'SideB',
  side_c: 'SideC',
  side_d: 'SideD',
  side_other_1: 'SideOther1',
  side_other_2: 'SideOther2',
  side_other_3: 'SideOther3',
  side_other_4: 'SideOther4',
};

export const enum FEEDBACK_LEVELS {
  costDriver = 'cost_driver',
  manufacturingIssue = 'manufacturing_issue',
  exclusion = 'complete_exclusion',
  information = 'information',
}

export const enum KEYS {
  /*eslint-disable camelcase*/
  counterbore = 'counterbore',
  countersink = 'countersink',
  deep_cut_planar = 'deep_cut_planar',
  deep_cut_radius = 'deep_cut_radius',
  deep_hole = 'deep_hole',
  exclusion = 'complete_exclusion',
  flat_part_drilled_hole = 'drilled_hole',
  flat_part_laser_pierce = 'laser_pierce',
  hole = 'hole',
  issue = 'issue',
  letter = 'letter',
  mill_group = 'mill_group',
  non_uniform_cavity = 'non_uniform_cavity',
  plane_chamfer = 'plane_chamfer',
  plane_draft = 'plane_draft',
  pocket = 'pocket',
  protrusion = 'non_uniform_protrusion',
  punch = 'punch',
  small_cut = 'small_cut',
  small_hole = 'small_hole',
  small_radius = 'small_radius',
  tight_corner = 'tight_corner',
  uncut_faces = 'uncut_faces',
  untracked_dfm = 'untracked_dfm',
  simplified_splines = 'simplified_splines',
  solidified_body = 'solidified_body',
  /*eslint-enable camelcase*/
}

export const COLORS = {
  white: 0xffffff,
  white_s: '#FFFFFF',
  silver: 0xc0c0c0,
  black: 0x000000,
  black_s: '#000000',
  maroon: 0x800000,
  olive: 0x808000,
  bright_green: 0x35f43b,
  soft_white: 0xfafafa,
  red: 0xff0000,
  red_s: '#C22C2C',
  green: 0x00ff00,
  green_s: '#008000',
  blue: 0x0000ff,
  yellow: 0xffff00,
  yellow_s: '#FCC118',
  highlight: 0x4dc0fd,
  cyan: 0x00ffff,
  purple: 0x8000ff,
  light_purple: 0xc561ff,
  pink: 0xff00e8,
  orange: 0xff8300,
  salmon: 0xfa8072,
  paperless_orange: 0xed6c24,
  gray: 0xa9afaf,
  gray_s: '#A9AFAF',
  turquoise: 0x2ad2e3,
  navy: 0x0f2897,
  soft_blue: 0x67d0f2,
};

export const temporarilySimplifiedColorHex = '#06D1DE';
export const temporarilySimplifiedColorSurfaceHex = '#EEF5FF';
export const tooComplexColorHex = '#DD6B20';
export const tooComplexPartViewerColorHex = '#f59300';
export const tooComplexColorSurfaceHex = '#DD6B20';
export const failedColorHex = '#E53E3E';
export const failedColorSurfaceHex = '#E53E3E';

export const STOCK_DEFAULTS = {
  color: 0xf2e381,
  opacity: 0.5,
};

// TODO: ALPHABETIZE AND REFERENCE OTHER KEYS
export const COLOR_CODES = {
  additionalLine: 0xa05200,
  EXCLUSION: 0xff4136,
  ISSUE: 0xffdc00,
  EXCLUDED_EDGE: COLORS.red,
  PART_COLOR: COLORS.gray,
  stl_part_color: 0xfffdfd,
  edge_color: 0x262626,
  text_color: 0x262626,
  scene_color: COLORS.soft_white,
  highlight_color: COLORS.highlight,
  edge_highlight_color: 0xfdfa29,
  stl_highlight_color: 0x55c5ff,
  selected_color: COLORS.bright_green,
  historical_selected_color: 0x4ca809, //green
  caliper_faces: 0x1f4eff,
  caliper_edges: 0x5fd0fe,
  caliper_vertices: 0x4ca809,
  stl_selected_color: 0x75f079,
  spotlight: COLORS.soft_white,
  assemblySelected: 0x4f9aff,
  X: 0xe95242,
  Y: 0x599773,
  Z: 0x3075d2,
  X_s: '#E95242',
  Y_s: '#599773',
  Z_s: '#3075D2',
  CUBE_BASE_COLOR: 0xffffff,
  CUBE_BORDER_COLOR: 0x999999,
  CUBE_HOVER_COLOR: 0x3584ff,
  CUBE_HOVER_COLOR_RGB: { r: 0.207, g: 0.516, b: 1 },
  CUBE_SELECT_COLOR_RGB: { r: 0.0625, g: 0.285, b: 0.629 },
  // CODES FOR FEATURES/FEEDBACK/FAMILIES
  [SETUP_LABELS.side_1]: COLORS.light_purple,
  [SETUP_LABELS.side_2]: COLORS.soft_blue,
  [SETUP_LABELS.side_a]: COLORS.green,
  [SETUP_LABELS.side_b]: COLORS.yellow,
  [SETUP_LABELS.side_c]: COLORS.olive,
  [SETUP_LABELS.side_d]: COLORS.orange,
  [SETUP_LABELS.side_other_1]: 0x7da3e6,
  [SETUP_LABELS.side_other_2]: 0x01a8eb,
  [SETUP_LABELS.side_other_3]: COLORS.navy,
  [SETUP_LABELS.side_other_4]: COLORS.pink,
  Setup: COLORS.soft_white,
  [KEYS.uncut_faces]: COLORS.black,
  [KEYS.letter]: COLORS.pink,
  [KEYS.hole]: COLORS.navy,
  Hole: COLORS.navy,
  [KEYS.pocket]: COLORS.orange,
  Character: COLORS.orange,
  Pocket: COLORS.orange,
  group: COLORS.turquoise,
  [KEYS.punch]: COLORS.green,
  [KEYS.exclusion]: COLORS.black,
  [KEYS.flat_part_drilled_hole]: COLORS.navy,
  'Simple Drilled Hole': COLORS.navy,
  piercing: COLORS.orange,
  Piercing: COLORS.orange,
  cutout_feature: COLORS.orange,
  tube_cutout_feature: COLORS.orange,
  tube_cutout_end: COLORS.green,
  'Cutout End': COLORS.green,
  'Cutout Feature': COLORS.orange,
  punch_multi_hit_feature: COLORS.orange,
  'Multi Hit Feature': COLORS.orange,
  parse_hole: COLORS.orange,
  [LEGACY_KEYS.flat_part_cavity]: COLORS.purple,
  [KEYS.counterbore]: COLORS.green,
  [KEYS.countersink]: COLORS.pink,
  lasered_countersink: COLORS.turquoise,
  'Lasered Countersink': COLORS.turquoise,
  Counterbore: COLORS.green,
  Countersink: COLORS.pink,
  lathe_external_cut: COLORS.bright_green,
  lathe_internal_cut: COLORS.navy,
  lathe_problem_faces: COLORS.yellow,
  lathe_derived_faces: COLORS.cyan,
  lathe_incompatible_faces: COLORS.red,
  lathe_cap_faces: COLORS.purple,
  lathe_problem_group: COLORS.paperless_orange,
  lathe_uncuttable_faces: COLORS.black,
  [SETUP_LABELS.lathe_setup1]: COLORS.light_purple,
  [SETUP_LABELS.lathe_setup2]: COLORS.cyan,
  coaxial_attack: COLORS.light_purple,
  antiaxial_attack: COLORS.cyan,
  perpendicular_attack: COLORS.green,
  Bend: COLORS.green,
  Offset: COLORS.olive,
  'Flat Part': COLORS.gray,
  pin: COLORS.cyan,
  core: COLORS.cyan,
  custom_core: COLORS.cyan,
  circular_pocket: COLORS.orange,
  'Circular Pocket': COLORS.orange,
  'Internal Cut': COLORS.orange,
  'External Cut': COLORS.navy,
  'Lathe Stock': STOCK_DEFAULTS.color,
  lathe_stock: STOCK_DEFAULTS.color,
  wire_edm_cut: COLORS.cyan,
  wire_edm_loop: COLORS.green,
  Cut: COLORS.cyan,
  Loop: COLORS.green,
  transition: COLORS.green,
  Transition: COLORS.green,
  bend_relief: COLORS.blue,
  'Bend (Split)': COLORS.cyan,
  'Bend Relief': COLORS.blue,
  Embossment: COLORS.blue,
  embossment: COLORS.blue,
  etching: COLORS.yellow,
  Etching: COLORS.yellow,
  'Single Hit Feature': COLORS.pink,
  punch_single_hit_feature: COLORS.pink,
  'Circular Punch': COLORS.turquoise,
  circular_punch: COLORS.turquoise,
  'Obround Punch': COLORS.pink,
  obround_punch: COLORS.pink,
  'Rectangular Punch': COLORS.purple,
  rectangular_punch: COLORS.purple,
  'Slot Punch': COLORS.olive,
  slot_punch: COLORS.olive,
  'Square Punch': COLORS.navy,
  square_punch: COLORS.navy,
  curl: COLORS.soft_blue,
  Curl: COLORS.soft_blue,
  'Tear Drop Hem': COLORS.soft_blue,
  'Open Hem': COLORS.soft_blue,
  'Live Tooling Hole': COLORS.green,
  'Live Tooling Protrusion': COLORS.orange,
  'Live Tooling Cavity': COLORS.purple,
  live_tooling_coaxial: COLORS.soft_blue,
  live_tooling_antiaxial: COLORS.bright_green,
  'Live Tooling Radial Direction': COLORS.light_purple,
  live_tooling_radial_direction: COLORS.light_purple,
  machined_counter_bore: COLORS.green,
  machined_counter_sink: COLORS.pink,
  machined_simple_hole: COLORS.navy,
  Pin: COLORS.green,
  'Machined Core': COLORS.purple,
  'Machined Custom Core': COLORS.soft_blue,
  weld_seam: COLORS.soft_blue,
  'Weld Seam': COLORS.soft_blue,
};

// Alias of the wire vocabulary so `dbData` rows from the API type-check against
// the formatters directly. Kept under the old name for its existing call sites.
export const FEATURE_PROP_TYPES = GEOMETRIC_ANALYSIS_PROP_TYPE;
export type FEATURE_PROP_TYPES = GeometricAnalysisPropType;

export const enum ADDITIONAL_DISPLAY_TYPES {
  TESSELLATION = 'tessellation',
  CURVILINEAR_EDGES = 'curvilinear_edges',
  LINES = 'lines',
  BOX = 'box',
  CYLINDER = 'cylinder',
  ARROW = 'arrow',
  CHECK_WIRES = 'check_wires',
  PARTIAL_FACE = 'partial_face',
  PARTIAL_FACE_ARRAY = 'partial_face_array',
  FEATURE_VOLUME_DISPLAY = 'feature_volume_display',
}

// indicates which additional display types will have actions explicitly
// associated with turning them on and off, and not necessarily linked to
// the turning on of the checkbox themselves
export const EXPLICIT_ADDITIONAL_DISPLAY_TYPES: ADDITIONAL_DISPLAY_TYPES[] = [
  ADDITIONAL_DISPLAY_TYPES.FEATURE_VOLUME_DISPLAY,
];

// lined out keys of additionalViewerContent that will indicate if this is a
// special additional display item to render as part of the setups UI
export const VOLUME_DISPLAY_KEYS = ['feature_volume_display'];

export const ENTITY_META_DATA_INFO: {
  key: string;
  type: FEATURE_PROP_TYPES;
  label: string;
  order?: number;
}[] = [
  {
    key: 'type',
    type: FEATURE_PROP_TYPES.STRING,
    label: 'Type',
  },
  {
    key: 'area',
    type: FEATURE_PROP_TYPES.LENGTH,
    label: 'Area',
    order: 2,
  },
  {
    key: 'length',
    type: FEATURE_PROP_TYPES.LENGTH,
    label: 'Length',
    order: 1,
  },
  {
    key: 'width',
    type: FEATURE_PROP_TYPES.LENGTH,
    label: 'Width',
    order: 1,
  },
  {
    key: 'height',
    type: FEATURE_PROP_TYPES.LENGTH,
    label: 'Height',
    order: 1,
  },
  {
    key: 'diameter',
    type: FEATURE_PROP_TYPES.LENGTH,
    label: 'Diameter',
    order: 1,
  },
  {
    key: 'major-diameter',
    type: FEATURE_PROP_TYPES.LENGTH,
    label: 'Major Diameter',
    order: 1,
  },
  {
    key: 'minor-diameter',
    type: FEATURE_PROP_TYPES.LENGTH,
    label: 'Minor Diameter',
    order: 1,
  },
  {
    key: 'large-diameter',
    type: FEATURE_PROP_TYPES.LENGTH,
    label: 'Large Diameter',
    order: 1,
  },
  {
    key: 'small-diameter',
    type: FEATURE_PROP_TYPES.LENGTH,
    label: 'Small Diameter',
    order: 1,
  },
  {
    key: 'angle',
    type: FEATURE_PROP_TYPES.ANGLE,
    label: 'Angle',
  },
  {
    key: 'semi-angle',
    type: FEATURE_PROP_TYPES.ANGLE,
    label: 'Semi-Angle',
  },
  {
    key: 'azimuthal-angle',
    type: FEATURE_PROP_TYPES.ANGLE,
    label: 'Azimuthal-Angle',
  },
  {
    key: 'radial-angle',
    type: FEATURE_PROP_TYPES.ANGLE,
    label: 'Radial-Angle',
  },
];

// TODO: have display names be driven by descriptor classes
export const FAMILY_MAP = {
  direction: 'Setup',
  machine_direction: 'Machine Direction',
  pocket: 'Pocket',
  group: 'Pocket',
  mill_group: 'Pocket',
  punch: 'Pocket',
  hole: 'Hole',
  circular_pocket: 'Circular Pocket',
  curl: 'Curl',
  piercing: 'Cutout Feature',
  cutout_feature: 'Cutout Feature',
  tube_cutout_feature: 'Cutout Feature',
  tube_cutout_end: 'Cutout End',
  lasered_countersink: 'Lasered Countersink',
  punch_single_hit_feature: 'Single Hit Feature',
  punch_multi_hit_feature: 'Multi Hit Feature',
  circular_punch: 'Circular Punch',
  obround_punch: 'Obround Punch',
  rectangular_punch: 'Rectangular Punch',
  slot_punch: 'Slot Punch',
  square_punch: 'Square Punch',
  countersink: 'Countersink',
  counterbore: 'Counterbore',
  drilled_hole: 'Simple Drilled Hole',
  character: 'Character',
  transition: 'Transition',
  bend_relief: 'Bend Relief',
  embossment: 'Embossment',
  etching: 'Etching',
  sheet_metal_flat_part: 'Flat Part',
  bend_item: 'Bend',
  bend: 'Bend',
  offset: 'Offset',
  lathe_setup: 'Machine Direction',
  lathe_attack: 'Setup',
  lathe_external_cut: 'External Cut',
  lathe_internal_cut: 'Internal Cut',
  lathe_stock: 'Lathe Stock',
  wire_edm_setup: 'Direction',
  wire_edm_cut: 'Cut',
  wire_edm_loop: 'Loop',
  tear_drop: 'Tear Drop Hem',
  open_hem: 'Open Hem',
  live_tooling_hole: 'Live Tooling Hole',
  live_tooling_cavity: 'Live Tooling Cavity',
  live_tooling_protrusion: 'Live Tooling Protrusion',
  live_tooling_radial_direction: 'Live Tooling Radial Direction',
  live_tooling_axial_direction: 'Live Tooling Axial Direction',
  machined_simple_hole: 'Simple Drilled Hole',
  machined_counter_bore: 'Counterbore',
  machined_counter_sink: 'Countersink',
  casting_pin: 'Pin',
  casting_core: 'Machined Core',
  casting_custom_core: 'Machined Custom Core',
  weld_seam: 'Weld Seam',
};

export const QUICK_DESCRIPTION = {
  [SETUP_LABELS.side_1]: 'Side 1',
  [SETUP_LABELS.side_2]: 'Side 2',
  [SETUP_LABELS.side_a]: 'Side A',
  [SETUP_LABELS.side_b]: 'Side B',
  [SETUP_LABELS.side_c]: 'Side C',
  [SETUP_LABELS.side_d]: 'Side D',
  [SETUP_LABELS.side_other_1]: 'Side Other 1',
  [SETUP_LABELS.side_other_2]: 'Side Other 2',
  [SETUP_LABELS.side_other_3]: 'Side Other 3',
  [SETUP_LABELS.side_other_4]: 'Side Other 4',
  [KEYS.uncut_faces]: 'Uncut Faces',
  [KEYS.hole]: 'Hole',
  tipped_hole: 'Tipped Hole',
  flat: 'Flat Bottom Hole',
  flat_bottom_hole: 'Flat Bottom Hole',
  [KEYS.letter]: 'Letter or Character',
  [KEYS.pocket]: 'Pocket',
  [KEYS.mill_group]: 'Mill Group',
  [KEYS.punch]: 'Punch',
  [KEYS.untracked_dfm]: 'Untracked',
  [KEYS.tight_corner]: 'Tight Corner',
  [KEYS.deep_hole]: 'Deep Hole',
  [KEYS.deep_cut_radius]: 'Deep Radiused Cut',
  [KEYS.deep_cut_planar]: 'Deep Exterior/Planar Cut',
  [KEYS.flat_part_drilled_hole]: 'Simple Drilled Hole',
  [KEYS.flat_part_laser_pierce]: 'Laser Pierce',
  [KEYS.counterbore]: 'Counterbore',
  [KEYS.countersink]: 'Countersink',
  [KEYS.small_cut]: 'Small Cut',
  [KEYS.small_hole]: 'Small Hole',
  [KEYS.small_radius]: 'Small Radius',
  [LEGACY_KEYS.small_cone]: 'Small Conical Face',
  lateral_tool_space: 'Insufficient Tool Space',
  no_lathe_axis: 'No Lathe Axis',
  lathe_uncuttable_faces: 'Uncuttable Faces',
  lathe_incompatible_faces: 'Live Tooling Required',
  lathe_external_cuts: 'External Cuts',
  lathe_internal_cuts: 'Internal Cuts',
  protrusion: 'Protrusion',
  infinitely_thin_feature: 'Infinitely Thin Feature',
  bend: 'Bend',
  offset: 'Offset',
  cut_near_bend: 'Cut Near Bend',
  no_root_plane: 'No Root Plane Found',
  no_bends_found: 'No Bends Found',
  bad_bend: 'Bad Bend',
  dirty_fuse: 'Flat Part Reconstruction Failed',
  circular_pocket: 'Circular Pocket',
  deep_circular_pocket: 'Deep Circular Pocket',
  assembly_warning: 'Assembly Warning',
  nested_loop: 'Nested Loop',
  open_loop: 'Open Loop',
  branching_loop: 'Branching Loop',
  intersecting_loops: 'Intersecting Loops',
  multiple_outer_contours: 'Multiple Parts in File',
  no_cut_geometry: 'No Cut Geometry',
  thickness: 'Part too thick',
  non_solid: 'Part Not Solid',
  non_solid_warning: 'Part Not Solid',
  partially_cut_face: 'Partially Cut Face',
  lathe_stock: 'Lathe Stock',
  wire_edm_loop: 'Loop',
  wire_edm_cut: 'Wire Cut',
  wire_edm_tight_corner: 'Tight Corner',
  one_away_cut: 'Flushing Issue, one away',
  two_away_cut: 'Flushing Issue, both away',
  wire_edm_angled_cut: 'Angled Cut',
  wire_edm_non_uniform_cut: 'Explicit 4-axis Operation',
  wire_intersection_warning: 'Wire Conflict',
  wire_edm_incompatible_faces: 'Incompatible Faces',
  machining_required: 'Additional Operations Required',
  small_hole_diameter: 'Small Hole Diameter',
  small_internal_radius: 'Small Internal Radius',
  slanted_hole: 'Slanted Hole',
  partial_hole: 'Partial Hole',
  hole_through_cavity: 'Hole Thru Cavity',
  transition: 'Transition Feature',
  convex_fillet: 'Convex Fillet',
  concave_fillet: 'Concave Fillet',
  chamfer: 'Chamfer',
  tapered_wall: 'Tapered Wall',
  short_bend: 'Short Bend',
  thin_bend_relief: 'Thin Bend Relief',
  shallow_bend_relief: 'Shallow Bend Relief',
  small_bend_radius: 'Small Bend Radius',
  large_bend_radius: 'Large Bend Radius',
  tight_curl: 'Tight Curl',
  close_countersink_bores: 'Close Countersinks/bores',
  countersink_bore_to_edge: 'Close Countersink/bore to Edge',
  cutout_to_edge: 'Close Cutout to Edge',
  close_cutouts: 'Close Cutouts',
  incompatible_tube: 'Incompatible Tube',
  cylindrical_close_cutouts: 'Close Cutouts',
  cylindrical_close_countersinks: 'Close Countersinks',
  angled_cut: 'Angled Cut',
  no_bend_relief: 'No Bend Relief',
  non_standard_bend_angle: 'Non-90-Degree Bend',
  tight_hem: 'Tight Hem',
  short_hem_return: 'Short Hem Return',
  different_bend_directions: 'Different Bend Directions',
  curl_near_bend: 'Curl Near Bend',
  hem_near_bend: 'Hem Near Bend',
  bore_hole_wo_relief: 'Bored Hole without Relief',
  lathe_slender_part: 'Slender Part',
  lathe_small_internal_rad: 'Small Internal Radius',
  lathe_steep_profile: 'Lathe Steep Profile',
  off_axis_hole: 'Off Axis Hole',
  asymmetric_cavity: 'Asymmetric Cavity',
  captive_part: 'Captive Part',
  no_corner_relief: 'No Corner Relief',
  nonconcentric_bend: 'Bend Issue: Nonconcentric',
  unequal_bend_angle: 'Bend Issue: Unequal Angles',
  flat_bend_unequal_thickness: 'Bend Issue: Unequal Thicknesses',
  ignored_body_in_shape: 'Ignored Body',
  machined_counter_bore: 'Counterbore',
  machined_counter_sink: 'Countersink',
  machined_simple_hole: 'Simple Drilled Hole',
  unfolding_issue: 'Unfolding Issue',
  neg_bend_allowance: 'Bend Allowance Issue',
  split_bend: 'Split Bend',
  milling_large_part: 'Dimensions Exceed Limits',
  lathe_long_part: 'Length Exceeds Limit',
  lathe_wide_part: 'Radius Exceeds Limit',
  exceeds_press_length: 'Bend Exceeds Press Length',
  milling_multi_body: 'Part Has Multiple Bodies',
  lathe_multi_body: 'Part Has Multiple Bodies',
  unfolded_sheet_metal_large_part: 'Dimensions Exceed Limits',
  sheet_metal_large_part: 'Dimensions Exceed Limits',
  healed_overlapping_edges: 'Healed Overlapping Edges',
  closed_open_loop: 'Closed Open Loop',
  unsimplified_spline_warning: 'Possible Unsimplified Spline(s) Detected',
  simplified_splines: 'Simplified Splines',
  solidified_body: 'Solidified Body',
  w_bend_warning: 'W-Bending May Be Required',
  short_flange: 'Short Flange',
  close_bends: 'Close Bends',
  // LEGACY KEYS BELOW, remove when possible
  pin: 'Pin',
  core: 'Machined Core',
  custom_core: 'Custom Machined Core',
  [LEGACY_KEYS.bend_item]: 'Bend',
  [LEGACY_KEYS.illegal_hole]: 'Illegal Hole',
  [LEGACY_KEYS.bounding_box]: 'Bounding Box Error',
  [LEGACY_KEYS.bad_faces]: 'Incompatible Faces (non uniform)',
  [LEGACY_KEYS.non_uniform]: 'Non Uniform Face',
  [LEGACY_KEYS.bounding_plane_insufficient]: 'Insufficent Bounding Planes',
  [LEGACY_KEYS.bounding_plane_excess]: 'More than two top and bottom planes',
  [LEGACY_KEYS.large_internal_plane]: 'Large Internal Plane',
  [LEGACY_KEYS.external_radius]: 'External Radius',
  [LEGACY_KEYS.non_vertical_cylinder]: 'Non-Vertical Cylinder',
  [LEGACY_KEYS.non_vertical_cone]: 'Non-Vertical Cone',
  [LEGACY_KEYS.bad_spline]: 'Bad BSpline Face',
  [LEGACY_KEYS.bad_bezier]: 'Bad Bezier Face',
  [LEGACY_KEYS.bad_extrusion]: 'Bad Extrusion Face',
  [LEGACY_KEYS.small_feature]: 'Small Feature',
  [KEYS.plane_draft]: 'Drafted Face',
  [KEYS.plane_chamfer]: 'Chamfered Face',
  [KEYS.protrusion]: 'Non-Uniform Protrusion',
  [KEYS.non_uniform_cavity]: 'Non-Uniform Cavity',
  [LEGACY_KEYS.unpaired_cone]: 'Unpaired Cone in hole feature',
  [LEGACY_KEYS.unpaired_frustum]: 'Unpaired Frustum in hole feature',
  [LEGACY_KEYS.tight_corner_mild]:
    'Tight Corner, Mild Angle (Less than 10 degrees)',
  [LEGACY_KEYS.cone_chamfer]: 'Chamfered Face',
  [LEGACY_KEYS.cone_draft]: 'Drafted Face',
  [LEGACY_KEYS.flat_part_cavity]: 'Cavity',
  [LEGACY_KEYS.tipped]: 'Tipped Hole',
  [LEGACY_KEYS.flat]: 'Flat Bottom Hole',
  [LEGACY_KEYS.slanted]: 'Slanted Hole',
};

export const DFM_DESCRIPTIONS = {
  [KEYS.tight_corner]:
    'The face connects to another face with a sharp internal corner. All corners must be radiused to be machined.',
  [KEYS.deep_hole]:
    'This hole requires a deep cut. The cut depth to tool diameter ratio is larger than the specified threshold.',
  deep_circular_pocket:
    'This circular pocket requires a deep cut. The cut depth to tool diameter ratio is larger than the specified threshold.',
  [KEYS.deep_cut_radius]:
    'This face requires a deep cut. The cut depth to tool diameter ratio is larger than the specified threshold.',
  [KEYS.deep_cut_planar]:
    'This face requires a deep cut. The cut depth to tool diameter ratio is larger than the specified threshold.',
  [KEYS.counterbore]:
    'Counterbores require special tooling in context of flat parts, some suppliers will not be able to manufacture this with flat parts processes.',
  [KEYS.countersink]:
    'Countersinks require special tooling in context of flat parts, some suppliers will not be able to manufacture this with flat parts processes.',
  [KEYS.flat_part_drilled_hole]:
    'Hole diameter is smaller than specified smallest cut size. Must be drilled by hand.',
  tipped_hole: 'Hole with a tipped bottom',
  flat_bottom_hole: 'Hole with a flat bottom',
  'Lateral Tool Space Issue':
    'A square cutting tool cannot cut this face because the tool is blocked by  connecting faces.',
  protrusion:
    'The protruding face is difficult to manufacture with traditional flat parts processes.',
  infinitely_thin_feature:
    'The drawn line represents an infinitely thin feature, or the coming together of two convex faces. This cannot be manufatured. A thickness must be given to this union.',
  pin: 'The body shown requires a pin to create a stable boss within a urethane casted mold.',
  core: 'The body shown requires a machined core to create a stable boss within a urethane casted mold.',
  custom_core:
    'The body shown requires a custom machined core to create a stable boss within a urethane casted mold.',
  circular_pocket:
    'A hole-like feature that has a characteristic diameter larger than specified threshold for holes.',
  assembly_warning: 'Part file has multiple bodies.',
  [KEYS.uncut_faces]: 'Faces cannot be cut with conventional 3-axis tooling.',
  nested_loop:
    'The part has nested loops. We will interpret the smallest loop in a nesting as the cutout, and any other larger loops as markings.',
  open_loop:
    'The part has open loops. We will interpret open loops as markings.',
  branching_loop:
    'The part has branching loops. We will interpret the branching loops as markings.',
  intersecting_loops:
    'The part has intersecting loops. We will interpret the intersecting loops as cut outs if they are not contained by other loops. If the intersected loops are contained by other loops, they will be interpreted as markings.',
  multiple_outer_contours:
    'The file contains more than one outer cut contour, so it cannot be priced as a single part. Split each part into its own file.',
  no_cut_geometry:
    'Nothing in the file is mapped as cut, so there is no part to price. Map the part outline as cut.',
  thickness: 'Part is too thick to be compatible with this process.',
  non_solid:
    'Part was interpreted as a shell. Part volume and face normal information are not reliable.',
  non_solid_warning:
    'Part was interpreted as a shell. Part volume and face normal information are not reliable.',
  partially_cut_face:
    'We could not determine an effective way to cut this face. The highlighted portion is the section of the face we do not know how to reach.',
  cut_near_bend:
    'Cut is too close to the bend. Distance is smaller than the specified threshold.',
  no_root_plane: 'No root planar face found to begin sheet metal unbending.',
  no_lathe_axis: 'No primary lathe axis was found to rotate the body about.',
  lathe_incompatible_faces:
    'Faces are not cuttable with traditional lathe tooling. Need live tooling.',
  lathe_uncuttable_faces:
    'Faces have a feature that is uncuttable with traditional tooling.',
  lateral_tool_space:
    'Face is not cuttable with traditional tooling. There is not enough room for the tool to cut the face.',
  wire_edm_tight_corner: 'This tight corner can cause the wire to break.',
  one_away_cut:
    'One flush port cannot maintain contact with the contour of the cut.',
  two_away_cut:
    'Both flush ports cannot maintain contact with the contour of the cut.',
  wire_edm_angled_cut:
    'This cut is more likely to cause breaks in the wire and will increase runtime.',
  wire_edm_non_uniform_cut:
    'This face needs to have explicit independent motion of both top and bottom profiles to cut.',
  wire_intersection_warning:
    'One or more ridge contours is self intersecting with the face. This face might have incompatible geometry for wire EDM machines.',
  wire_edm_incompatible_faces:
    'These faces are incompatible with EDM machines.',
  machining_required: 'Faces require additional operations to manufacture.',
  small_internal_radius: 'Radius is smaller than the machinable threshold.',
  small_hole_diameter: 'Diameter is smaller than the machinable threshold.',
  slanted_hole: 'Hole does not have perpendicular entry.',
  partial_hole:
    'Inconsistent drill contact could make this feature difficult to machine.',
  hole_through_cavity:
    'Inconsistent drill contact could make this feature difficult to machine.',
  transition: 'Faces represent transition from one feature to another.',
  convex_fillet:
    'Fillet that requires special tooling/strategy to manufacturing to tolerance and finish.',
  concave_fillet: 'Transition feature likely requiring a ball nose end mill',
  chamfer: 'Transition feature requires step milling.',
  tapered_wall: 'Feature requires step milling.',
  short_bend:
    'Flange is shorter than specified length to bend radius limit. Tool could leave a mark on the part.',
  thin_bend_relief: 'Bend relief is thinner than specified minimum threshold.',
  shallow_bend_relief:
    'Bend relief is shallower than specified minimum threshold.',
  small_bend_radius:
    'Radius of bend is smaller than specified minimum thickness ratio.',
  large_bend_radius:
    'Radius of bend is larger than specified maximum thickness ratio.',
  tight_curl:
    'Outer radius of curl is smaller than specified minimum thickness ratio.',
  close_countersink_bores:
    'Countersink/bore features are closer than the specified ratio to material thickness.',
  countersink_bore_to_edge:
    'Countersink/bore feature is too close to material edge.',
  close_cutouts:
    'Cutout features are closer than specified ratio to material thickness.',
  cutout_to_edge: 'Cutout is too close to material edge.',
  cylindrical_close_cutouts:
    'Cutout features are closer than specified ratio to material thickness.',
  cylindrical_close_countersinks:
    'Countersink features are closer than specified ratio to material thickness',
  incompatible_tube:
    'Part geometry is not compatible with tube laser interrogation',
  angled_cut:
    'Cut angle to Tube Axis is non-perpendicular. This cut requires additional axis capability.',
  no_bend_relief:
    'No bend relief present. Could cause local deformations when forming.',
  non_standard_bend_angle: 'Bend angle is not 90 degrees',
  tight_hem:
    'Hem radius is smaller than specified multiple of material thickness.',
  short_hem_return:
    'Hem return is shorter than specified multiple of thickness material thickness.',
  curl_near_bend:
    'Curl distance to bend is less than specified multiple of thickness plus bend radius plus curl radius.',
  hem_near_bend:
    'Hem distance to bend is less than specified multiple of thickness plus bend radius',
  different_bend_directions:
    'Plane has multiple bends in different bend directions.',
  bore_hole_wo_relief:
    'Bored hole relief depth to diameter is smaller than specified ratio.',
  lathe_slender_part:
    'This part has a length-to-minimum-outer-diameter ratio that exceeds specified threshold.',
  lathe_small_internal_rad:
    'The radius of this transition is smaller than the specified threshold.',
  lathe_steep_profile:
    'The angle between this profiled face and the lathe axis exceeds the specified threshold.',
  off_axis_hole:
    'This hole has a required machine direction that is not perpendicular nor parallel to the lathe axis.',
  asymmetric_cavity: 'This cavity is not symmetric about the lathe axis.',
  captive_part:
    'Part cannot be unfolded because the root is captive of one or more of its leaves.',
  no_corner_relief:
    'Corner between two bends has no relief. Could cause local deformations when forming.',
  nonconcentric_bend:
    'Cylinders of supposed bend are not concentric bend cannot be unfolded',
  unequal_bend_angle:
    'Cylinders of supposed bend have different angles bend cannot be unfolded',
  flat_bend_unequal_thickness:
    'Cylinders of supposed bend have different thickness than adjacent flat section bend cannot be unfolded',
  ignored_body_in_shape:
    'Body was ignored from the assembly breakout because it is not solid or not renderable. Entity is likely a design annotation.',
  unfolding_issue:
    'Part was not unfolded correctly no pricing created. Faces that could not be unfolded correctly are shown in red.',
  neg_bend_allowance:
    'Bend allowance calculated was negative due to small bend radius to thickness ratio. Unfolded model is likely incorrect. Check help documents for explanation of bend allowance determination.',
  split_bend:
    'Bend is made up of multiple distinct bend sections that can all be formed from the same press brake operation.',
  milling_large_part:
    'Part dimensions exceed the specified work envelope boundaries.',
  lathe_long_part:
    'Part length exceeds the specified work envelope boundaries.',
  lathe_wide_part:
    'Radius of part exceeds the specified work envelope boundaries.',
  exceeds_press_length: 'Bend length exceeds specified press working length.',
  milling_multi_body:
    'Part has multiple bodies; should be analyzed as an assembly.',
  lathe_multi_body:
    'Part has multiple bodies; should be analyzed as an assembly.',
  sheet_metal_large_part:
    'Part dimensions exceed the specified work envelope boundaries.',
  unfolded_sheet_metal_large_part:
    'Unfolded part dimensions exceed the specified work envelope boundaries.',
  healed_overlapping_edges:
    'We detected this part had duplicate or overlapping edges. These blemishes have been fixed. This automated healing is not expected to alter the design geometry, but the original geometry should be reviewed.',
  closed_open_loop:
    'An open loop was detected to have a negligible gap. This loop has been closed with a temporary edge to ensure downstream operations, like nesting, will work. Please review the original geometry for issues.',
  unsimplified_spline_warning:
    'Spline edges and faces have been detected. CAD analysis, such as feature detection and unfolding, is not available on spline entities. To resolve this, simplify spline entities.',
  w_bend_warning:
    'These two bends form a deep U shape. The length to bend separation ratio is greater than the specified W bending threshold.',
  short_flange:
    'The flange length to thickness ratio is smaller than specified threshold.',
  close_bends:
    'The bend separation to thickness ratio is smaller than specified threshold.',
};

//ActiveRepresentation is an internal enum for partviewer
export const enum ActiveRepresentation {
  FullDetailRep = 1,
  OBBSimplifiedRep = 2,
}
